import React, { useState } from 'react';
import { Box, Container, Typography, TextField, InputAdornment, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'gatsby';
import alioLogo from '../assets/images/logo/alio-logo.svg';
import subscribeIcon from '../assets/images/subscribe-icon.svg';
import linkedinIcon from '../assets/images/linkedin-icon.svg';
import facebookIcon from '../assets/images/facebook-icon.svg';
import instagramIcon from '../assets/images/instagram-icon.svg';
import twitterIcon from '../assets/images/twitter-icon.svg';
import ClutchWidgetSmall from './ClutchWidget';

const FooterLink = ({ to, children }) => (
  <Link 
    to={to} 
    style={{ 
      textDecoration: 'none',
      color: 'white',
      fontSize: '17px',
      fontFamily: 'Raleway',
      fontWeight: 400,
      lineHeight: '40px'
    }}
  >
    {children}
  </Link>
);

const ExternalLink = ({ href, children }) => (
  <a 
    href={href}
    style={{ 
      textDecoration: 'none',
      color: 'white',
      fontSize: '17px',
      fontFamily: 'Raleway',
      fontWeight: 400,
      lineHeight: '40px'
    }}
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
);

const FooterSection = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      // Here you would typically call an API to handle the subscription
      console.log('Subscribing email:', email);
      setSubscribed(true);
      setEmail('');
      setTimeout(() => {
        setSubscribed(false);
      }, 3000);
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        position: 'relative',
        zIndex: 1,
        py: 4,
        px: 2,
      }}
    >
      {/* Golden gradient line at top */}
      <Box
        sx={{
          width: '100%',
          height: '15px',
          left: 0,
          top: 0, // Adjust top position
          position: 'absolute',
          background: 'linear-gradient(130deg, #FFCC6D 0%, #C49634 100%)',
        }}
      />

      {/* Main content container */}
      <Container
        maxWidth="lg"
        sx={(theme) => ({
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '35px',
          [theme.breakpoints.down('md')]: {
            gap: '25px',
          },
        })}
      >
        {/* Links and Subscribe section */}
        <Box sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap', // Allow items to wrap on smaller screens
          gap: '30px', // Add gap for wrapped items
          [theme.breakpoints.down('sm')]: {
            gap: '40px',
          },
        })}>
          {/* Links container - to keep three columns together on mobile */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: { 
              xs: '30px', // Mobile gap
              sm: '60px', // Tablet gap
              md: '100px' // Desktop gap - significantly increased
            },
            [theme.breakpoints.down('sm')]: {
              width: '100%',
              justifyContent: 'space-between',
              gap: '10px', // Smaller gap on mobile to fit columns
            },
          })}>
            {/* Services Links */}
            <Box sx={(theme) => ({
              minWidth: { 
                xs: '90px', 
                sm: '143px' 
              },
              width: { 
                xs: '30%', // 1/3 width on mobile
                sm: 'auto'  // Natural width on desktop
              },
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              [theme.breakpoints.down('sm')]: {
                width: '30%', // Approx 1/3 of space on mobile
                minWidth: '90px',
              },
            })}>
              <Typography sx={{ color: 'white', fontSize: '16px', fontFamily: 'Raleway', fontWeight: 900 }}>
                Services
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink to="/services">Services</FooterLink>
                <FooterLink to="/expertise">Expertise</FooterLink>
                <FooterLink to="/white-papers">White Papers</FooterLink>
              </Box>
            </Box>

            {/* Company Links */}
            <Box sx={(theme) => ({
              minWidth: { 
                xs: '90px', 
                sm: '143px' 
              },
              width: { 
                xs: '30%', // 1/3 width on mobile
                sm: 'auto'  // Natural width on desktop
              },
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              [theme.breakpoints.down('sm')]: {
                width: '30%', // Approx 1/3 of space on mobile
                minWidth: '90px',
              },
            })}>
              <Typography sx={{ color: 'white', fontSize: '16px', fontFamily: 'Raleway', fontWeight: 900 }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink to="/company">About Us</FooterLink>
                <FooterLink to="/careers-opportunity">Careers</FooterLink>
                <FooterLink to="/community">Community</FooterLink>
                <ExternalLink href="https://clutch.co/profile/alio-it-solutions">
                  Clutch Profile
                </ExternalLink>
              </Box>
            </Box>

            {/* Resources Links */}
            <Box sx={(theme) => ({
              minWidth: { 
                xs: '90px', 
                sm: '143px' 
              },
              width: { 
                xs: '30%', // 1/3 width on mobile
                sm: 'auto'  // Natural width on desktop
              },
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              [theme.breakpoints.down('sm')]: {
                width: '30%', // Approx 1/3 of space on mobile
                minWidth: '90px',
              },
            })}>
              <Typography sx={{ color: 'white', fontSize: '16px', fontFamily: 'Raleway', fontWeight: 900 }}>
                Resources
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <FooterLink to="/case-studies">Case Studies</FooterLink>
                <FooterLink to="/blog">Blog Posts</FooterLink>
                <ExternalLink href="https://www.youtube.com/@alioit4373">
                  YouTube Channel
                </ExternalLink>
              </Box>
            </Box>
          </Box>

          {/* Subscribe Box */}
          <Box
            component="form"
            onSubmit={handleSubscribe}
            sx={(theme) => ({
              padding: '25px',
              background: 'linear-gradient(130deg, rgba(255, 182, 109, 0.25) 0%, rgba(218.53, 148.53, 68.53, 0.25) 100%)',
              borderRadius: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              width: 'auto', // Allow width to adjust
              maxWidth: '300px',
              [theme.breakpoints.down('sm')]: {
                width: '100%', // Take full width on small screens
                maxWidth: 'none', // Remove max width limit
                alignItems: 'center', // Center content inside
              },
            })}
          >
            <Typography sx={{ color: 'white', fontSize: '20px', fontFamily: 'Raleway', fontWeight: 600, lineHeight: '28px' }}>
              Subscribe
            </Typography>
            <TextField
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              type="email"
              required
              sx={{
                width: '100%', // Take full width of parent
                maxWidth: '248px',
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: '6px',
                  border: '1px solid rgba(255, 255, 255, 0.50)',
                  color: '#BBBBBB',
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                  '&:hover fieldset': {
                    borderColor: 'transparent',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'transparent',
                  },
                  '& input': {
                    color: '#BBBBBB',
                    fontSize: '16px',
                    fontFamily: 'Raleway',
                    padding: '10px',
                    '&::placeholder': {
                      color: '#BBBBBB',
                      opacity: 1,
                    },
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      type="submit" 
                      edge="end"
                      aria-label="subscribe"
                      sx={{ p: 0 }}
                    >
                      <Box component="img" src={subscribeIcon} sx={{ width: '24px', height: '24px' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {subscribed && (
              <Typography
                sx={{
                  color: '#6DFCFF',
                  fontSize: '14px',
                  fontFamily: 'Raleway',
                  fontWeight: '400',
                  lineHeight: '20px',
                }}
              >
                Thank you for subscribing!
              </Typography>
            )}
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '16px',
                fontFamily: 'Raleway',
                fontWeight: '400',
                lineHeight: '25px',
                wordWrap: 'break-word',
              }}
            >
              Hello, we are Alio IT. Your Remote Team but Better.
            </Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ height: '1px', bgcolor: 'white' }} />

        {/* Bottom section with logo and links */}
        <Box sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '40px',
          [theme.breakpoints.down('md')]: {
            gap: '30px',
          },
        })}>
          {/* Logo, social links and Clutch widget */}
          <Box sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: '20px',
            [theme.breakpoints.down('sm')]: {
              flexDirection: 'column',
              justifyContent: 'center',
            },
          })}>
            {/* Logo and Clutch widget container */}
            <Box sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              flex: '1',
              [theme.breakpoints.up('md')]: {
                justifyContent: 'flex-start',
                maxWidth: '50%',
              },
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                alignItems: 'center',
                gap: '30px',
                width: '100%'
              },
            })}>
              <Link to="/" style={{ display: 'block' }}>
                <Box component="img" src={alioLogo} />
              </Link>
              <Box 
                className="footer-widget" 
                sx={{ 
                  [theme.breakpoints.down('sm')]: {
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    '& > div': {
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center'
                    }
                  }
                }}
              >
                <ClutchWidgetSmall />
              </Box>
            </Box>
            {/* Social Media Icons and Privacy Link */}
            <Box sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '30px',
              [theme.breakpoints.up('md')]: {
                justifyContent: 'flex-end',
                flex: '1',
              },
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column-reverse',
                gap: '20px',
                width: '100%',
              },
            })}>
              {/* Privacy Link */}
              <FooterLink to="/privacy">Privacy</FooterLink>
              
              {/* Social Media Links */}
              <Box sx={{ 
                display: 'flex', 
                gap: '15px',
                [theme.breakpoints.down('sm')]: {
                  justifyContent: 'center',
                },
              }}>
                <ExternalLink href="https://www.linkedin.com/company/alio-nearshore-it/">
                  <Box component="img" src={linkedinIcon} sx={{ cursor: 'pointer' }} />
                </ExternalLink>
                <ExternalLink href="https://www.facebook.com/alioits/">
                  <Box component="img" src={facebookIcon} sx={{ cursor: 'pointer' }} />
                </ExternalLink>
                <ExternalLink href="https://www.instagram.com/alio_it/">
                  <Box component="img" src={instagramIcon} sx={{ cursor: 'pointer' }} />
                </ExternalLink>
                <ExternalLink href="https://x.com/alio_it">
                  <Box component="img" src={twitterIcon} sx={{ cursor: 'pointer' }} />
                </ExternalLink>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FooterSection; 